/**
 * Canvas 2D renderer for the isometric pixel world.
 * Handles clearing, depth sorting, and drawing all entities.
 */

import type { Camera } from "./Camera.js";
import type { Entity } from "../entities/Entity.js";

/** Background color matching the app's dark theme. */
const BG_COLOR = "#1a2a15";

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private camera: Camera;

  constructor(canvas: HTMLCanvasElement, camera: Camera) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to get 2D context");
    this.ctx = ctx;
    this.camera = camera;
    this.disableSmoothing();
  }

  /** Must be called after canvas resize (resize resets context state). */
  disableSmoothing(): void {
    this.ctx.imageSmoothingEnabled = false;
  }

  /** Draw a ground shadow ellipse under an entity. */
  private drawShadow(ctx: CanvasRenderingContext2D, entity: Entity): void {
    ctx.save();
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = "#000";
    ctx.beginPath();
    // Ellipse centered at entity's world position, slightly below
    ctx.ellipse(
      entity.worldX,
      entity.worldY + 2,
      14,  // horizontal radius
      5,   // vertical radius (flattened for isometric)
      0,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.restore();
  }

  /** Render all entities with depth sorting (painter's algorithm). */
  render(entities: Entity[]): void {
    const { ctx } = this;
    // The context is pre-scaled by devicePixelRatio (see PixelWorldCanvas
    // resizeCanvas), so all drawing — including the camera transform — must use
    // CSS/logical pixels, not the device-pixel backing store. Using the backing
    // store here pushes the transform origin off-screen at any DPR > 1.
    const dpr = window.devicePixelRatio || 1;
    const width = ctx.canvas.width / dpr;
    const height = ctx.canvas.height / dpr;

    // Clear
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, width, height);

    // Apply camera transform
    ctx.save();
    this.camera.applyTransform(ctx, width, height);

    // Depth sort: lower depth values drawn first (back), higher drawn last (front)
    const sorted = [...entities].sort((a, b) => a.depth - b.depth);

    // Draw each entity (shadow first, then sprite)
    for (const entity of sorted) {
      if (!entity.visible) continue;
      if (entity.castsShadow) {
        this.drawShadow(ctx, entity);
      }
      entity.draw(ctx);
    }

    ctx.restore();
  }
}
